/**
 * 任务 SSE 连接管理
 * 负责管理与后端的实时任务事件流连接
 */
import { SSEManager } from '@/utils/sse-manager'
import { generateBrowserFingerprint } from '@/utils/app-runtime'
import type { TaskEvent } from '@/types/task.events'

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error'

export class TaskSSEService {
    private connection: SSEManager<TaskEvent> | null = null
    private status: ConnectionStatus = 'disconnected'
    private messageHandlers: Set<(event: TaskEvent) => void> = new Set()
    private statusHandlers: Set<(status: ConnectionStatus) => void> = new Set()
    private debugEnabled = import.meta.env.DEV

    constructor() {
        // 单例模式或依赖注入通常更好
    }

    /**
     * 获取当前连接状态
     */
    getStatus(): ConnectionStatus {
        return this.status
    }

    /**
     * 订阅状态变化
     */
    onStatusChange(handler: (status: ConnectionStatus) => void) {
        this.statusHandlers.add(handler)
        return () => this.statusHandlers.delete(handler)
    }

    /**
     * 订阅消息事件
     */
    onMessage(handler: (event: TaskEvent) => void) {
        this.messageHandlers.add(handler)
        return () => this.messageHandlers.delete(handler)
    }

    /**
     * 更新状态并通知
     */
    private setStatus(newStatus: ConnectionStatus) {
        if (this.status !== newStatus) {
            this.status = newStatus
            this.statusHandlers.forEach(h => h(newStatus))
        }
    }

    /**
     * 建立连接
     */
    connect(): Promise<void> {
        // 如果已经连接或正在连接，直接返回
        if (this.connection) {
            const metrics = this.connection.getMetrics()
            if (metrics.connectionState === 'connected' || metrics.connectionState === 'connecting') {
                return Promise.resolve()
            }
            this.disconnect()
        }

        return new Promise((resolve) => {
            // EventSource不支持自定义头，通过URL参数传递指纹和token
            const fingerprint = generateBrowserFingerprint()
            const token = localStorage.getItem('auth_token')
            let url = `/api/v1/tasks/stream?fingerprint=${encodeURIComponent(fingerprint)}`
            if (token) {
                url += `&token=${encodeURIComponent(token)}`
            }

            // 设置超时（10秒）
            const timeout = setTimeout(() => {
                resolve() // 即使超时也 resolve，不阻塞业务
            }, 10000)

            this.setStatus('connecting')

            this.connection = new SSEManager<TaskEvent>({
                url,
                withCredentials: true,
                debug: this.debugEnabled,
                onOpen: () => {
                    clearTimeout(timeout)
                    this.setStatus('connected')
                    resolve()
                },
                onClose: () => {
                    this.setStatus('disconnected')
                },
                onError: (err) => {
                    clearTimeout(timeout)
                    console.error('[TaskSSEService] SSE 连接错误:', err)
                    this.setStatus('error')
                    // 不 reject，避免阻塞
                    resolve()
                }
            })

            // 监听消息
            this.connection.on('*', (event: TaskEvent) => {
                this.messageHandlers.forEach(h => h(event))
            })

            // 明确监听服务端发送的 ping 事件，通过拦截更新 SSEManager 内部的 lastMessageAt，避免由于不属于 message 事件导致的假死超时
            this.connection.on('ping', () => {
                // 用于触发底层 sse-manager 的活跃时间更新，无须其他处理
            })
        })
    }

    /**
     * 断开连接
     */
    disconnect() {
        if (this.connection) {
            this.connection.close()
            this.connection = null
            this.setStatus('disconnected')
        }
    }

    /**
     * 检查是否已连接
     */
    isConnected(): boolean {
        if (!this.connection) return false
        return this.connection.getMetrics().connectionState === 'connected'
    }
}

// 导出单例
export const taskSSEService = new TaskSSEService()

// Vite HMR 热更新时自动清理旧的 SSE 连接，防止产生多个假死连接
if (import.meta.hot) {
    import.meta.hot.dispose(() => {
        taskSSEService.disconnect()
    })
}
